const BASE_URL = import.meta.env.VITE_API_URL;

/**
 * A failed request, carrying the status so callers can tell "that code is not valid" (404) from "your session
 * expired" (401) from "something broke" (5xx). Those three need different words in front of a guest.
 */
export class HttpError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(status: number, body: unknown) {
        super(detailFrom(body) ?? `HTTP ${status}`);
        this.status = status;
        this.body = body;
    }
}

/**
 * The API answers errors with RFC 7807 ProblemDetails. Only `detail` is worth reading — `title` is the generic
 * status name ("Bad Request"), which tells a guest nothing the status code did not.
 *
 * Note the API deliberately omits `detail` outside development, so this is usually undefined in production and
 * callers must have their own wording ready.
 */
function detailFrom(body: unknown): string | undefined {
    if (!body || typeof body !== 'object') return undefined;

    const problem = body as { detail?: unknown };
    return typeof problem.detail === 'string' && problem.detail.trim() ? problem.detail.trim() : undefined;
}

interface RequestOptions {
    method?: 'GET' | 'POST';
    body?: unknown;
    /** A guest session token from POST /sessions. Omitted on the anonymous routes. */
    token?: string | null;
    signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    if (!BASE_URL) {
        throw new Error('VITE_API_URL is not configured.');
    }

    const headers: Record<string, string> = {};
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (options.token) headers['Authorization'] = `Bearer ${options.token}`;

    const response = await fetch(`${BASE_URL.replace(/\/$/, '')}${path}`, {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
    });

    // An empty 204 is a valid success and has no body to parse.
    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json')
        ? await response.json().catch(() => null)
        : await response.text().catch(() => '');

    if (!response.ok) throw new HttpError(response.status, payload);

    return payload as T;
}
